import { useCallback, useMemo, useRef, useState } from 'react';

type DialogSetOpenFunction<T = void> = {
  (nextOpen: false): void;
  (nextOpen: true, data: T): void;
};

interface DialogOpenState<T = void> {
  isOpen: true;
  data: T;
}
interface DialogCloseState {
  isOpen: false;
  data: undefined;
}

export type DialogState<T = void> = {
  setIsOpen: DialogSetOpenFunction<T>;
  open(data: T): void;
  close(): void;
  subscribe(listener: (event: DialogOpenEvent) => void): () => void;
} & (DialogOpenState<T> | DialogCloseState);

export interface UseDialogOptions {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?(open: boolean): void;
}

export type DialogOpenEvent = CustomEvent<{ open: boolean }>;

export default function useDialog<T = void>(
  options?: UseDialogOptions
): DialogState<T> {
  const [open, setOpen] = useState(options?.defaultOpen ?? false);
  const [data, setData] = useState<T | undefined>(undefined);
  const isOpen = options?.open ?? open;

  const listeners = useRef<((event: DialogOpenEvent) => void)[]>([]);
  const subscribe = useCallback(
    (listener: (event: DialogOpenEvent) => void) => {
      listeners.current.push(listener);
      return () => {
        listeners.current = listeners.current.filter((l) => l !== listener);
      };
    },
    []
  );

  const handleOpenChange = useCallback<DialogSetOpenFunction<T>>(
    (nextOpen: boolean, data?: T): void => {
      const event = new CustomEvent('dialog:open', {
        cancelable: true,
        detail: { open: nextOpen },
      });
      listeners.current.forEach((listener) => listener(event));
      if (event.defaultPrevented) return;
      setOpen(nextOpen);
      setData(data);
      options?.onOpenChange?.(nextOpen);
    },
    [options?.onOpenChange]
  );

  const toggleOpen = useCallback(
    (data: T) => handleOpenChange(true, data),
    [handleOpenChange]
  );

  const toggleClose = useCallback(
    () => handleOpenChange(false),
    [handleOpenChange]
  );

  return useMemo(() => {
    if (isOpen) {
      return {
        isOpen: true,
        data: data as T,
        setIsOpen: handleOpenChange,
        open: toggleOpen,
        close: toggleClose,
        subscribe,
      };
    } else {
      return {
        isOpen: false,
        data: undefined,
        setIsOpen: handleOpenChange,
        open: toggleOpen,
        close: toggleClose,
        subscribe,
      };
    }
  }, [isOpen, data, handleOpenChange, toggleOpen, toggleClose, subscribe]);
}
